/**
 * Hash-keyed, refcounted, LRU GPU-asset cache (Graphics G0 + G5).
 *
 * Dedupes loaded render assets by content hash (a repeat load is a cache hit, no second
 * fetch). G0 refcounts + disposes; G5 makes it a RETAINING LRU cache: when the last
 * reference is released the entry stays cache-warm (refcount 0) for reuse, and
 * `evictToBudget` disposes the LEAST-RECENTLY-USED unreferenced entries under memory
 * pressure (`evictToBudget(0)` reclaims all unreferenced — the world-clear / leak path).
 * `evict`/`clear` force-dispose. Each entry carries a VRAM estimate (the budget metric) +
 * a last-access clock (the LRU key).
 *
 * The disposer is INJECTED (not imported) so this module stays free of any runtime
 * `three` import — it bundles into a Node unit harness with a spy disposer, while
 * `AssetModule` wires the real engine `disposeObject3D`.
 */
import type * as THREE from 'three/webgpu';

export type DisposeFn = (object: THREE.Object3D) => void;

interface CacheEntry {
  object: THREE.Object3D;
  refs: number;
  vramMb: number;
  lastAccess: number;
}

export class AssetCache {
  private readonly map = new Map<string, CacheEntry>();
  private clock = 0;

  constructor(private readonly disposeFn: DisposeFn) {}

  has(hash: string): boolean { return this.map.has(hash); }
  size(): number { return this.map.size; }
  refs(hash: string): number { return this.map.get(hash)?.refs ?? 0; }

  /** Cache hit: bump the refcount + touch the LRU clock; null if absent. */
  acquire(hash: string): THREE.Object3D | null {
    const e = this.map.get(hash);
    if (!e) return null;
    e.refs += 1;
    e.lastAccess = ++this.clock;
    return e.object;
  }

  /**
   * Insert a freshly-loaded object at refcount 1. If a concurrent load already populated
   * the hash, dispose the loser and return the cached winner (dedup). `vramMb` is the
   * asset's VRAM estimate (the budget metric).
   */
  put(hash: string, object: THREE.Object3D, vramMb = 0): THREE.Object3D {
    const existing = this.map.get(hash);
    if (existing) {
      this.disposeFn(object); // a duplicate concurrent load — drop it
      existing.refs += 1;
      existing.lastAccess = ++this.clock;
      return existing.object;
    }
    this.map.set(hash, { object, refs: 1, vramMb, lastAccess: ++this.clock });
    return object;
  }

  /**
   * Release one reference. The entry is RETAINED at refcount 0 (cache-warm) for reuse —
   * eviction is deferred to `evictToBudget` (LRU under memory pressure) or `evict`/`clear`.
   * Returns true if the refcount dropped to 0 (now evictable).
   */
  release(hash: string): boolean {
    const e = this.map.get(hash);
    if (!e) return false;
    if (e.refs > 0) e.refs -= 1;
    return e.refs <= 0;
  }

  /** Force-dispose + evict regardless of refcount. */
  evict(hash: string): boolean {
    const e = this.map.get(hash);
    if (!e) return false;
    this.disposeFn(e.object);
    this.map.delete(hash);
    return true;
  }

  /** Total resident VRAM estimate (MB) across all cached entries (referenced + warm). */
  residentVramMb(): number { let mb = 0; for (const e of this.map.values()) mb += e.vramMb; return mb; }

  /** Number of cache-warm (refcount-0) entries currently evictable. */
  unreferencedCount(): number { let n = 0; for (const e of this.map.values()) if (e.refs <= 0) n += 1; return n; }

  /**
   * LRU eviction: while resident VRAM exceeds the budget, dispose the least-recently-used
   * UNREFERENCED (refcount 0) entries. Referenced (in-use) entries are never evicted, so a
   * heavy visible scene can exceed the budget — that's bounded by the server LI cap upstream.
   * Returns the number evicted.
   */
  evictToBudget(budgetMb: number): number {
    // budget ≤ 0 means "reclaim ALL unreferenced" (world-clear / leak path) — evict regardless of
    // the VRAM estimate, so an un-estimated (0 MB) asset can't become immortal in the cache.
    if (budgetMb > 0 && this.residentVramMb() <= budgetMb) return 0;
    const evictable = [...this.map.entries()]
      .filter(([, e]) => e.refs <= 0)
      .sort((a, b) => a[1].lastAccess - b[1].lastAccess); // oldest first
    let evicted = 0;
    for (const [hash, e] of evictable) {
      if (budgetMb > 0 && this.residentVramMb() <= budgetMb) break;
      this.disposeFn(e.object);
      this.map.delete(hash);
      evicted += 1;
    }
    return evicted;
  }

  /** Dispose everything (module teardown). */
  clear(): void {
    for (const e of this.map.values()) this.disposeFn(e.object);
    this.map.clear();
  }
}
